import {
  StatsDataFragment,
  StatsHypothesisResultFragment,
  StatsV2AnalysisGuardrailStatus,
  StatsV2AnalysisQualityStatus,
  StatsV2AnalysisShippingRecommendation,
  StatsV2AnalysisSuccessStatus,
  StatsV2AnalysisTankingStatus,
  getTypeOrNull,
  isError,
} from '@spotify/confidence-graphql';

import { getMetricAnnotations } from '../hooks/useStatsData';
import {
  AnalysisResult,
  AnnotationV2,
  ComparisonRecommendationV2,
} from '../types';

const SRM_HYPOTHESIS_ID = 'srm';

export function convertAnalysisResults(
  analysisResults: StatsDataFragment['analysisResults'],
): AnalysisResult[] {
  const mappedAnalysisResults = analysisResults
    ?.filter(r => !isError(r.analysisResult))
    .map(r => {
      const analysisResult = getTypeOrNull(r.analysisResult);
      return {
        ...r,
        analysisResult: {
          ...analysisResult,
          results: (analysisResult?.results ?? []).filter(
            result => result.id !== SRM_HYPOTHESIS_ID,
          ),
        },
      };
    });

  const convertedAnalysisResults = mappedAnalysisResults?.map(
    analysisResultForFilter => {
      let results: StatsHypothesisResultFragment[] | undefined;
      let metricAnnotations: Record<string, AnnotationV2[]> | undefined;
      let recommendations: ComparisonRecommendationV2[] | undefined;
      if (analysisResultForFilter) {
        results = analysisResultForFilter.analysisResult?.results;
        metricAnnotations = getMetricAnnotations(results);

        if (
          analysisResultForFilter.analysisResult?.treatments === undefined ||
          analysisResultForFilter.analysisResult.treatments.length === 0
        ) {
          recommendations = [];
        } else {
          const baseline =
            analysisResultForFilter.analysisResult.treatments.find(
              treatment => !treatment.recommendation,
            )?.id ?? 'baseline';
          recommendations = analysisResultForFilter.analysisResult.treatments
            .filter(treatment => treatment.recommendation)
            .map(treatment => {
              return {
                baseline: baseline,
                compared: treatment.id ?? 'unknown',
                recommendation:
                  treatment.recommendation?.shipping ??
                  StatsV2AnalysisShippingRecommendation.ShippingRecommendationWaitForData,
                tanking: {
                  status:
                    treatment.recommendation?.status?.tanking ??
                    StatsV2AnalysisTankingStatus.TankingStatusNoMetrics,
                },
                guardrail: {
                  status:
                    treatment.recommendation?.status?.guardrail ??
                    StatsV2AnalysisGuardrailStatus.GuardrailStatusNoGuardrails,
                },
                success: {
                  status:
                    treatment.recommendation?.status?.success ??
                    StatsV2AnalysisSuccessStatus.SuccessStatusNoSuccessMetric,
                },
                quality: {
                  status:
                    treatment.recommendation?.status?.quality ??
                    StatsV2AnalysisQualityStatus.QualityStatusAllSucceeded,
                },
              };
            });
        }
      }
      return {
        results: results ?? [],
        annotations: metricAnnotations ?? {},
        recommendations: recommendations ?? [],
        exposureFilter: analysisResultForFilter.exposureFilterName ?? undefined,
      };
    },
  );
  return convertedAnalysisResults ?? [];
}
